import React, { useState, useRef, useEffect } from 'react'
import {
  IconButton,
  Dialog,
  DialogContent,
  CircularProgress,
} from '@material-ui/core'
import { UploadImageIcon, DeleteIcon } from 'app/components/icons'
import CloseIcon from '@material-ui/icons/Close'
import apiConfig from 'app/configs/apiConfigs'
import effects from 'app/effects/effects'
import useQueue from 'app/componentV4/useQueue'
import { resizeAvatar_250 } from 'commons/resizeImage'
import apiConfigs from 'app/configs/apiConfigs'

const uploadFile = async ({ file }) => {
  const config = await effects.apis.getUploadImageConfig()
  const response = await fetch(config.url, {
    method: 'PUT',
    headers: {
      'Content-Type': file.type,
    },
    body: file,
  })

  if (!response.ok) {
    throw new Error(`Upload failed with status ${response.status}`)
  }
  return { ...config }
}

const ImageUploadButton = ({ index, imageUrl, onImageUpload }) => {
  const fileInputRefs = useRef([])
  const { addTask, processQueue, isProcessing } = useQueue()

  // Use ref to always call the latest onImageUpload callback
  const onImageUploadRef = useRef(onImageUpload)

  useEffect(() => {
    onImageUploadRef.current = onImageUpload
  }, [onImageUpload])

  const [open, setOpen] = useState(false)

  const uploadAndUpdate = async (file) => {
    const result = await uploadFile({ file })
    // Use the ref to ensure we call the latest callback
    await onImageUploadRef.current(index, result.imagePath)
  }

  const handleFileChange = (event) => {
    const file = event.target.files[0]
    if (!file) return

    // Add upload task to the queue
    addTask(() => uploadAndUpdate(file))
  }

  const handleClickImage = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const handleDeleteImage = () => {
    onImageUploadRef.current(index, null)
    setOpen(false)
  }

  const openFileInput = () => {
    if (fileInputRefs.current[index]) {
      fileInputRefs.current[index].click()
    }
  }

  useEffect(() => {
    if (!isProcessing) {
      processQueue()
    }
  }, [processQueue, isProcessing])

  return (
    <div>
      {imageUrl ? (
        <div className="w-48">
          <img
            src={`${apiConfigs.cndUrl}/${resizeAvatar_250(imageUrl)}`}
            alt={`Uploaded ${index}`}
            className="w-48 h-48 p-0 flex justify-center items-center cursor-pointer hover:border-primary rounded-8"
            onClick={handleClickImage}
          />

          <Dialog
            open={open}
            onClose={handleClose}
            fullScreen
            PaperProps={{
              style: { backgroundColor: 'transparent' },
            }}
          >
            <DialogContent className="bg-black h-full p-0">
              <div className="w-screen h-screen flex flex-col">
                <div className="div1 h-50 flex items-center">
                  <IconButton
                    aria-label="close"
                    className="text-white"
                    onClick={handleClose}
                  >
                    <CloseIcon />
                  </IconButton>
                </div>

                <div className="flex-1 flex items-center justify-center overflow-hidden px-10 py-5">
                  <img
                    src={`${apiConfigs.cndUrl}/${resizeAvatar_250(imageUrl)}`}
                    alt="Preview"
                    className="w-full h-full max-w-full max-h-full object-contain"
                  />
                </div>
                <div className=" h-50 flex items-center justify-center">
                  <IconButton
                    aria-label="delete"
                    onClick={handleDeleteImage}
                    className="text-white"
                  >
                    <DeleteIcon classes="w-32" />
                  </IconButton>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      ) : (
        <div className="flex-1 w-full mb-1 sm:mb-0 bg-white rounded-8 border font-medium border border-grey-input">
          <input
            accept="image/*"
            className="hidden"
            ref={(el) => (fileInputRefs.current[index] = el)}
            type="file"
            onChange={handleFileChange}
            disabled={isProcessing}
          />
          {isProcessing ? (
            <div className="w-48 h-48 rounded-8 flex justify-center items-center">
              <CircularProgress size={24} style={{ color: '#00B2AD' }} />
            </div>
          ) : (
            <IconButton
              component="span"
              className="w-48 h-48 p-0 cursor-pointer px-5"
              onClick={openFileInput}
              disabled={isProcessing}
            >
              <UploadImageIcon className="w-full h-full" />
            </IconButton>
          )}
        </div>
      )}
    </div>
  )
}

export default ImageUploadButton
